import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import {
  Trophy,
  Crown,
  Users,
  Sparkles,
  Medal,
  Shield,
} from "lucide-react";
import ClanLogo from "../components/clan/ClanLogo";

export default function LeaderboardPage() {
  const [clans, setClans] = useState([]);
  const [players, setPlayers] = useState([]);
  const [tab, setTab] = useState("clans");

  useEffect(() => {
    api.get("/leaderboard/clans").then((r) => setClans(r.data));
    api.get("/leaderboard/players").then((r) => setPlayers(r.data));
  }, []);

  const data = tab === "clans" ? clans : players;

  return (
    <div className="space-y-8" dir="rtl">
      {/* ── Header ── */}
      <div className="text-center max-w-2xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold-500/10 border border-gold-500/30 text-gold-500 text-xs font-bold tracking-widest uppercase">
          <Trophy size={14} className="animate-pulse" />
          لائحة المتصدرين
        </div>

        <h1 className="font-display font-black text-4xl md:text-5xl text-white">
          التصنيف العام
        </h1>

        <p className="text-white/50 text-sm">
          استعرض أفضل الكلانات واللاعبين في المنصة والأكثر تحقيقاً للانتصارات.
        </p>
      </div>

      {/* ── Tabs Selector ── */}
      <div className="flex justify-center">
        <div className="inline-flex items-center bg-[#111113]/90 border border-white/10 rounded-2xl p-1.5 shadow-xl">
          <button
            data-testid="tab-lb-clans"
            onClick={() => setTab("clans")}
            className={`px-6 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition-all duration-300 ${
              tab === "clans"
                ? "bg-gold-500 text-black shadow-[0_0_15px_rgba(255,204,0,0.3)] font-black"
                : "text-white/50 hover:text-white hover:bg-white/5"
            }`}
          >
            <Shield size={16} />
            أفضل الكلانات
          </button>

          <button
            data-testid="tab-lb-players"
            onClick={() => setTab("players")}
            className={`px-6 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition-all duration-300 ${
              tab === "players"
                ? "bg-gold-500 text-black shadow-[0_0_15px_rgba(255,204,0,0.3)] font-black"
                : "text-white/50 hover:text-white hover:bg-white/5"
            }`}
          >
            <Users size={16} />
            أفضل اللاعبين
          </button>
        </div>
      </div>

      {/* ── Leaderboard Table/Cards ── */}
      <div className="bg-[#111113]/90 border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
        <table className="w-full text-right">
          <thead className="bg-white/5 border-b border-white/10 text-xs uppercase font-bold text-white/40">
            <tr>
              <th className="py-4 px-6 w-16 text-center">
                المركز
              </th>

              <th className="py-4 px-6">
                {tab === "clans" ? "الكلان" : "اللاعب"}
              </th>

              <th className="py-4 px-6 text-center">
                فوز / خسارة
              </th>

              <th className="py-4 px-6 w-24 text-center font-mono">
                W/L
              </th>

              <th className="py-4 px-6 text-end w-32">
                النقاط
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-white/5">
            {data.map((row, idx) => {
              const wins = row.wins || 0;
              const losses = row.losses || 0;

              const kd =
                row.kd != null
                  ? row.kd
                  : losses === 0
                  ? wins.toFixed(2)
                  : (wins / losses).toFixed(2);

              const isPlus =
                tab === "clans"
                  ? row.is_clan_plus || row.is_plus
                  : row.is_personal_plus;

              return (
                <tr
                  key={row.id}
                  data-testid={`lb-row-${idx}`}
                  className="hover:bg-white/[0.02] transition-colors group"
                >
                  {/* Rank Column */}
                  <td className="py-4 px-6 text-center">
                    <div
                      className={`inline-grid place-items-center h-10 w-10 rounded-xl font-display font-black text-sm shrink-0 ${
                        idx === 0
                          ? "bg-gradient-to-b from-gold-300 via-gold-500 to-gold-700 text-black shadow-[0_0_15px_rgba(255,204,0,0.4)]"
                          : idx === 1
                          ? "bg-gradient-to-b from-gray-200 via-gray-400 to-gray-600 text-black shadow-[0_0_12px_rgba(229,231,235,0.3)]"
                          : idx === 2
                          ? "bg-gradient-to-b from-amber-600 via-amber-700 to-amber-900 text-white shadow-[0_0_12px_rgba(180,83,9,0.3)]"
                          : "bg-white/5 text-white/50 border border-white/10"
                      }`}
                    >
                      {idx === 0 ? (
                        <Crown
                          size={18}
                          className="text-black"
                        />
                      ) : idx === 1 ? (
                        <Medal
                          size={18}
                          className="text-black"
                        />
                      ) : idx === 2 ? (
                        <Medal
                          size={18}
                          className="text-white"
                        />
                      ) : (
                        idx + 1
                      )}
                    </div>
                  </td>

                  {/* Name Column */}
                  <td className="py-4 px-6">
                    {tab === "clans" ? (
                      <Link
                        to={`/clans/${row.id}`}
                        className="inline-flex items-center gap-3 hover:opacity-80 transition group/link"
                      >
                        <ClanLogo
                          clan={{
                            logo:
                              row.logo ||
                              row.logo_url ||
                              row.avatar,
                          }}
                          className="h-12 w-12 rounded-xl border border-gold-500/20 bg-gold-500/10 grid place-items-center shrink-0 shadow-md"
                          fallbackIconSize={20}
                        />

                        <div className="flex flex-col min-w-0">
                          <div className="flex items-center gap-1.5 font-display font-black text-white text-base group-hover/link:text-gold-400 transition">
                            <span className="truncate">
                              {row.name}
                            </span>

                            {isPlus && (
                              <Sparkles
                                size={13}
                                className="text-[#d4af37] shrink-0"
                              />
                            )}
                          </div>

                          <span className="text-xs font-mono text-white/40 mt-0.5">
                            [{row.tag}]
                          </span>

                          {/* Badges / Trophies */}
                          <div className="flex items-center gap-1 mt-1.5">
                            {(row.badges || [])
                              .slice(0, 3)
                              .map((b) => (
                                <span
                                  key={b.id}
                                  title={b.label}
                                  className="inline-flex h-4 min-w-[16px] items-center justify-center rounded border border-gold-400/50 bg-gold-500 px-1 text-[9px] font-black uppercase text-black shadow-sm"
                                >
                                  {(() => {
                                    const label = String(
                                      b.label ||
                                        b.kind ||
                                        ""
                                    ).trim();

                                    if (!label) {
                                      return "B";
                                    }

                                    if (/^admin$/i.test(label)) {
                                      return "A";
                                    }

                                    if (
                                      /^(prev|plus|personal)/i.test(
                                        label
                                      )
                                    ) {
                                      return "P";
                                    }

                                    return label[0].toUpperCase();
                                  })()}
                                </span>
                              ))}
                          </div>
                        </div>
                      </Link>
                    ) : (
                      <Link
                        to={`/players/${row.id}`}
                        data-testid={`lb-player-${row.id}`}
                        className="inline-flex items-center gap-3 hover:opacity-80 transition group/link"
                      >
                        <div className="h-10 w-10 rounded-xl bg-gradient-to-b from-purple-500/20 to-purple-600/10 border border-purple-500/30 grid place-items-center text-purple-300 font-display font-black shadow-sm shrink-0">
                          {row.username?.[0]?.toUpperCase() ||
                            "?"}
                        </div>

                        <div className="flex flex-col min-w-0">
                          <div className="flex items-center gap-1.5 font-bold text-white text-sm group-hover/link:text-purple-300 transition">
                            <span className="truncate">
                              {row.username}
                            </span>

                            {isPlus && (
                              <Crown
                                size={13}
                                className="text-gold-500 shrink-0"
                              />
                            )}
                          </div>
                        </div>
                      </Link>
                    )}
                  </td>

                  {/* W/L Ratio Raw */}
                  <td className="py-4 px-6 text-center">
                    <div className="inline-flex items-center gap-1.5 bg-black/40 px-3 py-1 rounded-lg border border-white/5">
                      <span className="text-emerald-400 font-bold text-sm">
                        {wins}
                      </span>

                      <span className="text-white/20 text-xs">
                        /
                      </span>

                      <span className="text-destructive font-bold text-sm">
                        {losses}
                      </span>
                    </div>
                  </td>

                  {/* W/L Rating */}
                  <td className="py-4 px-6 text-center font-mono font-bold text-white/70">
                    {kd}
                  </td>

                  {/* Total Points */}
                  <td className="py-4 px-6 text-end">
                    <div className="inline-flex flex-col items-end justify-center">
                      <span className="font-display font-black text-xl text-gold-500 drop-shadow-sm">
                        {row.points || 0}
                      </span>

                      <span className="text-[9px] uppercase tracking-widest text-white/40">
                        Points
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}

            {/* Empty State */}
            {data.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="py-16 text-center"
                >
                  <Trophy
                    size={32}
                    className="mx-auto text-white/20 mb-3"
                  />

                  <p className="text-white/40 text-sm">
                    لا توجد بيانات متوفرة بعد في لائحة
                    المتصدرين.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
